import cn from 'classnames';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useMemo, useState, useReducer, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import Async from 'react-select/async';
import Select from 'react-select';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { EMPTY_ACTORS_RESULT, showActorSearchError } from '../../utils/actorSearchError';
import {
  DEFAULT_STATUSES,
  buildActorsQuery,
  buildFilterBody,
  effectiveStatuses,
  isGroupMember,
  selectableSources,
} from './actorPickerQuery';
import { Loader } from '../../components/Loader/Loader';
import { SourcesTable } from '../../components/SourcesTable/SourcesTable';
import { Search } from '../../components/Search/Search';
import { CheckboxField } from '../../components/Checkbox/CheckboxField';
import Tooltip from '../../components/Tooltip/Tooltip';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { Filter } from '../SourcesPage/Filter/Filter';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { Radiobutton } from '../../components/DropdownMenu/Radiobutton';
import { SortingOrder } from '../../components/DropdownMenu/SortingOrder';
import { Tabs } from '../../components/Tabs/Tabs';
import { ReactComponent as ArrowIcon } from '../NarrativePage/assets/arrow.svg';
import { ReactComponent as ArrowDownIcon } from '../NarrativePage/assets/arrowDown.svg';
import { ReactComponent as SortIcon } from '../../assets/sort.svg';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { ReactComponent as CloseIcon } from '../SearchPage/assets/close.svg';
import { ReactComponent as AddToIcon } from '../../assets/addTo.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete.svg';
import Switch from '../../components/Switch';
import { AddToSourceGroupModal } from '../../components/AddToSourceGroupModal/AddToSourceGroupModal';

import styles from './GroupPageEdit.module.scss';
import tableStyles from '../../components/SourcesTable/SourcesTable.module.scss';
import bulkPanelStyles from '../NarrativePage/NarrativePage.module.scss';
import { Tooltip as ReactTooltip } from 'react-tooltip';


// The picker's list call fails fast: a search that needs longer is too broad to be useful.
const PICKER_LIST_TIMEOUT_MS = 60_000;

const sortingFieldNamesBack = {
  id: 'ID',
  audience: 'AUDIENCE',
  name: 'ACTOR',
  source_type: 'PLATFORM',
  status: 'STATUS',
  url: 'URL',
  country: 'COUNTRY',
  relevance: 'RELEVANCE',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'APPLY_FILTER': {
      return Object.assign({}, state, {
        platforms: action.payload.platforms,
        countries: action.payload.countries,
        audience_lte: action.payload.audience_lte,
        audience_gte: action.payload.audience_gte,
        status: action.payload.status,
        affiliationCountries: action.payload.affiliationCountries,
        IoCs: action.payload.IoCs,
        IoCsExclude: action.payload.IoCsExclude,
        showOnlyBots: action.payload.showOnlyBots,
        sourceTags: action.payload.sourceTags,
        sourceTagsExclude: action.payload.sourceTagsExclude,
        affiliationCountriesExclude: action.payload.affiliationCountriesExclude,
        countriesExclude: action.payload.countriesExclude,
        audienceExclude: action.payload.audienceExclude,
        geoGroup: action.payload.geoGroup,
        geoGroupExclude: action.payload.geoGroupExclude,
        botsExclude: action.payload.botsExclude,
        affiliationTypes: action.payload.affiliationTypes,
        affiliationTypesExclude: action.payload.affiliationTypesExclude,
        page: action.payload.page,
      });
    }

    case 'UPDATE_QUERY': {
      return Object.assign({}, state, {
        searchQuery: action.payload,
      });
    }

    case 'SORT_BY': {
      return Object.assign({}, state, {
        sorting: { ...state.sorting, fieldName: action.fieldName },
      });
    }

    case 'IS_ASCENDING': {
      return Object.assign({}, state, {
        sorting: { ...state.sorting, isAscending: action.isAscending },
      });
    }

    case 'CHANGE_PAGE':
      return Object.assign({}, state, {
        page: action.page,
      });
    
    case 'CHANGE_TAB':
      return Object.assign({}, state, {
        tabIndex: action.tabIndex,
      });
    default:
      return state;
  }
};

export const GroupPageEdit = () => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [sources, setSources] = useState(null);
  const [dropdownMenu, setDropdownMenu] = useState(false);
  const [error, setError] = useState(null);
  const [isSavingGroupInfo, setIsSavingGroupInfo] = useState(false);
  const [selectedSources, setSelectedSources] = useState([]);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({isActive: false, group: null, ids: []});
  const [copyGroupModal, setCopyGroupModal] = useState({ isActive: false, sources: [] });
  const [areAllSourcesSelected, setAreAllSourcesSelected] = useState(false);
  const [isPopulating, setIsPopulating] = useState(false);
  const populationPollRef = useRef(null);
  const [name, setName] = useState(group?.name || '');
  const [privacySettings, setPrivacySettings] = useState('PRIVATE');
  const [description, setDescription] = useState(group?.description || '');
  const [workspaces, setWorkspaces] = useState(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);


  useEffect(() => {
    API.fetch('GET', `/API/v1/groups/${groupId}`)
      .then((data) => {
        setGroup(data)
        setName(data?.name)
        setPrivacySettings(data?.visibility ? data?.visibility : 'PRIVATE')
        setDescription(data?.description)
        setSelectedWorkspaces(data?.workspace_ids)
      })
      .catch((error) => {
        setError(error.message);
        showRequestError(error, t('Actor group'));
      });
  }, [groupId]);

  const [state, dispatch] = useReducer(reducer, {
    sorting: {
      isAscending: false,
      fieldName: sortingFieldNamesBack['relevance'],
    },
    searchQuery: '',
    platforms: [],
    countries: [],
    audience_lte: null,
    audience_gte: null,
    status: DEFAULT_STATUSES,
    affiliationCountries: [],
    IoCs: [],
    IoCsExclude: false,
    showOnlyBots: false,
    sourceTags: [],
    sourceTagsExclude: false,
    affiliationCountriesExclude: false,
    countriesExclude: false,
    audienceExclude: false,
    geoGroup: [],
    geoGroupExclude: false,
    botsExclude: false,
    affiliationTypes: [],
    affiliationTypesExclude: false,
    page: 1,
    tabIndex: 0,
  });

  const privacySettingsOptions = [
    {value: 'PRIVATE', label: t('private_female', {context: 'female'})},
    {value: 'PUBLIC', label: t('public_female', {context: 'female'})},
    {value: 'SHARABLE', label: t('sharable_female', {context: 'female'})},
  ];

  const fetchAllWorkspaces = useCallback(
    () => {
      if (!currentUser?.is_super_admin) {
        return;
      }
      const urlParams = new URLSearchParams();

      urlParams.set('size', '20');
      urlParams.set('sorting', '-id');

      API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
        (data) => {
          let workspaces = data.objects
            .map((workspace) => {
              return { value: workspace.id, label: workspace.name };
            });
          setWorkspaces(workspaces);
        },
      ).catch((e) => showRequestError(e, t('Workspaces')));
    },
    [],
  );

  useEffect(fetchAllWorkspaces, [fetchAllWorkspaces]);

  const fetchWorkspaces = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (!currentUser?.is_super_admin) {
          return;
        }
        const urlParams = new URLSearchParams();

        if (inputValue) {
          urlParams.set('q', inputValue);
        }

        API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
          (data) => {
            callback(
              data.objects
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((workspace) => {
                  return { value: workspace.id, label: workspace.name };
                }),
            );
          },
        ).catch((e) => showRequestError(e, t('Workspaces')));
      }, 1000),
    [],
  );

  const workspacesPromiseOptions = (inputValue) => {
    if (!inputValue) {
      return [];
    }
    return new Promise((resolve) => {
      fetchWorkspaces(inputValue, resolve);
    });
  };

  const fetchSelectedWorkspaces = (selectedWorkspaces) => {
    if (!currentUser?.is_super_admin) {
      return;
    }
    
    if (!selectedWorkspaces || selectedWorkspaces?.length === 0) {
      return [];
    }
    const urlParams = new URLSearchParams();
    selectedWorkspaces.forEach((workspace) => {
      urlParams.append('ids', workspace);
    });

    API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
      (data) => {
        let workspaces = data.objects
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((workspace) => {
            return { value: workspace.id, label: workspace.name };
          });
        setSelectedWorkspaces(workspaces);
      },
    ).catch((e) => showRequestError(e, t('Workspaces')));
  };

  useEffect(() => {
    fetchSelectedWorkspaces(group?.workspace_ids);
  }, [group]);

  useEffect(() => {
    return () => {
      if (populationPollRef.current) clearInterval(populationPollRef.current);
    };
  }, []);

  useEffect(() => {
    setAreAllSourcesSelected(false);
  }, [state]);

  const handleEditGroup = (name, privacySettings, workspaces, description, group) => {
    setIsSavingGroupInfo(true);
    API.fetch('PATCH', `/API/v1/groups/${group.id}`, null, {
      name: name,
      visibility: privacySettings,
      workspace_ids: selectedWorkspaces?.map((workspace) => workspace.value),
      description: description,
    }).then(() => {
      toast.success(t('Group info saved'));
      API.fetch('GET', `/API/v1/groups/${groupId}`).then(setGroup).catch((e) => showRequestError(e, t('Actor group')));
    }).catch((e) => showRequestError(e, t('Edit group')))
      .finally(() => setIsSavingGroupInfo(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSavingGroupInfo) return;

    if(name.length === 0) {
      setError('Please fill in this field')
      return
    }

    handleEditGroup(name, privacySettings, workspaces, description, group)
  }

  const page = searchParams.get('page') ?? 1;

  // The one list request of this page. Everything it depends on is in this string, so
  // the effect below fetches when (and only when) the request would actually differ.
  const listQuery = useMemo(
    () => buildActorsQuery(state, page, groupId),
    [state, page, groupId],
  );

  const abortControllerRef = useRef(null);
  const lastFetchedQueryRef = useRef(null);

  const loadSources = useCallback(
    (query) => {
      // a newer request supersedes the one in flight
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const isCurrent = () => abortControllerRef.current === abortController;

      setSources(null);

      API.fetch(
        'GET',
        `/API/v2/actors?${query}`,
        null,
        null,
        abortController.signal,
        undefined,
        undefined,
        PICKER_LIST_TIMEOUT_MS,
      )
        .then((data) => {
          if (isCurrent()) setSources(data);
        })
        .catch((e) => {
          if (!isCurrent()) return;
          if (showActorSearchError(e, t('Actors'))) setSources(EMPTY_ACTORS_RESULT);
        });
    },
    [t],
  );

  useEffect(() => {
    // "Edit group info" shows no list: nothing to load there. The query is still
    // remembered, so coming back to "Add sources" refetches only if it changed.
    if (state.tabIndex !== 0) return;
    if (lastFetchedQueryRef.current === listQuery) return;
    lastFetchedQueryRef.current = listQuery;
    setSelectedSources([]);
    loadSources(listQuery);
  }, [listQuery, state.tabIndex, loadSources]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  // Refetch the list as it is now (after actors were added), from callbacks that may
  // run long after the render that created them (the populate poll).
  const latestListRef = useRef({ query: listQuery, tabIndex: state.tabIndex });
  latestListRef.current = { query: listQuery, tabIndex: state.tabIndex };
  const refetchSources = useCallback(() => {
    const { query, tabIndex } = latestListRef.current;
    if (tabIndex !== 0) {
      lastFetchedQueryRef.current = null; // load fresh data when the user comes back
      return;
    }
    lastFetchedQueryRef.current = query;
    loadSources(query);
  }, [loadSources]);

  const checkIsSelected = (source) => {
    return selectedSources?.some((item) => item.id === source.id);
  };

  const selectedSourcesHandler = (source, value) => {
    if (value) {
      setSelectedSources([...selectedSources, source]);
    } else {
      setSelectedSources(selectedSources?.filter((item) => item.id !== source.id));
    }
  };

  const clearAllSelectedSources = () => {
    setSelectedSources([]);
  };

  // "Select all on page" covers only the actors that can still be added.
  const areAllIdsSelected = (sources, selectedSources) => {
    const selectable = selectableSources(sources);
    if (!selectable.length) return false;
    return selectable.every((source) => selectedSources?.some((item) => item.id === source.id));
  };

  const toggleAll = (sources, selectedSources) => {
    if (areAllIdsSelected(sources, selectedSources)) {
      setSelectedSources([]);
    } else {
      setSelectedSources(selectableSources(sources));
    }
  };
  const handleAddingSourcesToGroup = (sources) => {
    if (areAllSourcesSelected) {
      setIsPopulating(true);

      const filterBody = buildFilterBody(state);

      API.fetch('POST', `/API/v2/actor-groups/${groupId}/sources/from-filter`, null, filterBody)
        .then(() => {
          const interval = setInterval(() => {
            API.fetch('GET', `/API/v1/groups/${groupId}`)
              .then((data) => {
                if (data.population_status === 'DONE') {
                  clearInterval(interval);
                  populationPollRef.current = null;
                  setIsPopulating(false);
                  setAreAllSourcesSelected(false);
                  clearAllSelectedSources();
                  toast.success(t('Actors added to the Actor group'));
                  refetchSources();
                } else if (data.population_status === 'FAILED') {
                  clearInterval(interval);
                  populationPollRef.current = null;
                  setIsPopulating(false);
                  toast.error(t('Data import has failed. Try to create this group again.'));
                }
              })
              .catch((e) => {
                clearInterval(interval);
                populationPollRef.current = null;
                setIsPopulating(false);
                showRequestError(e, t('Add actors to group'));
              });
          }, 2000);
          populationPollRef.current = interval;
        })
        .catch((e) => {
          setIsPopulating(false);
          showRequestError(e, t('Add actors to group'));
        });
      return;
    }

    const sourceIds = sources?.map((source) => source.id);
    API.fetch('POST', `/API/v1/groups/${groupId}/sources`, null, {
      source_ids: sourceIds,
    }).then(() => {
      setSelectedSources([]);
      toast.success(t('Actors added to the Actor group'));
      refetchSources();
    }).catch((e) => showRequestError(e, t('Add actors to group')));
  };

  const buttonsOnScroll = () => {
    if(Object.keys(selectedSources).length > 0) {
      const buttons = document.getElementById('bulk');
      let scrolly = window.pageYOffset;
      if (!buttons) {
        return
      }

      if(scrolly > 150) {
        buttons.classList.add(bulkPanelStyles.floating)
      } else {
        buttons.classList.remove(bulkPanelStyles.floating)
      }
    } else {
      return
    }
  }

  useEffect(() => {
      window.addEventListener('scroll', buttonsOnScroll);
      window.addEventListener('mousemove', buttonsOnScroll);

    return() => {
      window.removeEventListener('scroll', buttonsOnScroll);
      window.removeEventListener('mousemove', buttonsOnScroll);
    }
  })

  if (!group && error) {
    return <div className="noData">{t(error)}</div>;
  }

  if (!group) {
    return <Loader />;
  }

  const sortingMenu = [
    {
      name: t('Identifier'),
      value: 'ID',
    },
    {
      name: t('Audience'),
      value: 'AUDIENCE',
    },
    {
      name: t('Platform'),
      value: 'PLATFORM',
    },
    {
      name: t('Name'),
      value: 'ACTOR',
    },
    {
      name: t('Link'),
      value: 'URL',
    },
    {
      name: t('Country'),
      value: 'COUNTRY',
    },
    {
      name: t('Status'),
      value: 'STATUS',
    },
    {
      name: t('Relevance'),
      value: 'RELEVANCE',
    },
  ]

  const sortingOrder = [
    {
      name: t('A-Z'),
      icon: <ArrowIcon/>,
      value: true,
    },
    {
      name: t('Z-A'),
      value: false,
      icon: <ArrowDownIcon/>,
    },
  ]

  const tabslist = [
    {
      title: t('Add sources'),
      content: (
        <>
          <div className={styles.searchWrapper}>
            <Search
              className={styles.searchInput}
              onChange={value => {
                dispatch({type: 'UPDATE_QUERY', payload: value});
                dispatch({type: 'CHANGE_PAGE', page: 1})
                setSearchParams({ q: value, page: 1});
              }}
            />

            <DropdownMenu  
              isOpen={dropdownMenu}
              header={t('Sort by')}
              onClick={() => setDropdownMenu(!dropdownMenu)}
              buttonName={t('Sort')}
              icon={<SortIcon/>}
            >
              <Radiobutton 
                itemsList={sortingMenu}
                current={state.sorting.fieldName}
                onChange={(value) => dispatch({type: 'SORT_BY', fieldName: value})}
              />

              <SortingOrder 
                itemsList={sortingOrder}
                onClick={value => dispatch({type: 'IS_ASCENDING', isAscending: value})}
                current={state.sorting.isAscending}
              />
            </DropdownMenu>


            <Filter
              state={state}
              onChange={(
                platforms,
                countries,
                audience,
                status,
                affiliationCountries,
                IoCs,
                showOnlyBots,
                sourceTags,
                affiliationCountriesExclude,
                countriesExclude,
                audienceExclude,
                geoGroup,
                geoGroupExclude,
                botsExclude,
                IoCsExclude,
                sourceTagsExclude,
                affiliationTypes,
                affiliationTypesExclude,
              ) => {
              dispatch({type: 'APPLY_FILTER',
              payload: {
                platforms: platforms,
                countries: countries,
                audience_lte: audience[1],
                audience_gte: audience[0],
                affiliationCountries: affiliationCountries,
                IoCs: IoCs,
                IoCsExclude: IoCsExclude,
                showOnlyBots: showOnlyBots,
                sourceTags: sourceTags,
                sourceTagsExclude: sourceTagsExclude,
                affiliationCountriesExclude: affiliationCountriesExclude,
                countriesExclude: countriesExclude,
                audienceExclude: audienceExclude,
                geoGroup: geoGroup,
                geoGroupExclude: geoGroupExclude,
                botsExclude: botsExclude,
                affiliationTypes: affiliationTypes,
                affiliationTypesExclude: affiliationTypesExclude,
                // an empty selection falls back to the default, and the Filter shows it
                status: effectiveStatuses(status),
                page: 1,
              }})
              if (state.searchQuery) {
                setSearchParams({ q: state.searchQuery, page: 1});
              } else {
                setSearchParams({ page: 1});
              }
            }}
            />
          </div>
          {group?.owned_by_workspace && areAllIdsSelected(sources?.objects, selectedSources) && sources?.objects?.length > 0 && (
            <div className={styles.selectAllBanner}>
              {areAllSourcesSelected ? (
                <>
                  <span>{t('allSourcesSelected', { count: sources?.total })}</span>
                  <button
                    type="button"
                    className={styles.selectAllLink}
                    onClick={() => { setAreAllSourcesSelected(false); clearAllSelectedSources(); }}
                  >
                    {t('Clear selection')}
                  </button>
                </>
              ) : (
                <>
                  <span>{t('allPageSourcesSelected', { count: selectableSources(sources?.objects).length })}</span>
                  <button
                    type="button"
                    className={styles.selectAllLink}
                    onClick={() => setAreAllSourcesSelected(true)}
                  >
                    {t('selectAllSources', { count: sources?.total })}
                  </button>
                </>
              )}
            </div>
          )}
          <SourcesTable
            rowClassName={styles.tableRow}
            sources={sources}
            withHeader={false}
            searchQuery={state.searchQuery}
            checkIsSelected={checkIsSelected}
            renderPrefixRow={(source) => {
              const isSelected = checkIsSelected(source);

              if (! group?.owned_by_workspace) {
                return <td></td>;
              }

              const isMember = isGroupMember(source);
              return (
                <td className={tableStyles.tableCheckbox}>
                  <CheckboxField
                  className={cn(styles.tableRow, { [styles.memberCheckbox]: isMember })}
                  checked={isMember || isSelected}
                  isDisabled={isMember}
                  onChange={(value) => {
                    if (!isMember) selectedSourcesHandler(source, value);
                  }}
                />
                </td>
              );
            }}
            selectAllCheckbox={() => {
              if (! group?.owned_by_workspace) {
                return <td></td>;
              }
              return (
                <td className={tableStyles.tableCheckbox}>
                  <CheckboxField
                    className={styles.tableRow}
                    checked={areAllIdsSelected(sources?.objects, selectedSources)}
                    onChange={() => {toggleAll(sources?.objects, selectedSources)}}
                  />
                </td>
                
              );
            }}
            controls={(source) => {
              if (! group?.owned_by_workspace) {
                return '';
              }
              if (isGroupMember(source)) {
                return <span className={cn(tableStyles.tag, styles.inGroupBadge)}>{t('In group')}</span>;
              }
              return (
                <button
                  className='tableActionBtn'
                  onClick={() => handleAddingSourcesToGroup([source])}
                  data-tooltip-id="add-source"
                  data-tooltip-content={t('Add')}
                >
                  <AddToIcon />
                </button>
              );
            }}
            onPageChange={(page) => {
              dispatch({type: 'CHANGE_PAGE', page: page})
            }}
          />
        </>
      )
    },
    {
      title: t('Edit group info'),
      content: (
        <form method="post" onSubmit={e => handleSubmit(e)}>
          <div className={error ? 'form-element error' : 'form-element'}>
            <label htmlFor="name">{t('Name')}</label>
            <input
              className=""
              id="name"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className={error ? 'form-element error' : 'form-element'}>
            <label htmlFor="description">{t('Description')}</label>
            <textarea
              id="description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {currentUser?.is_super_admin && (
            <div className="form-element">
              <label className={styles.label} htmlFor="privacy-settings">
                {t('Privacy settings')}
              </label>
              <Select
                id="privacy-settings"
                name="privacy-settings"
                options={privacySettingsOptions}
                value={privacySettingsOptions.find(
                  (option) => option.value === privacySettings,
                )}
                onChange={(option) => setPrivacySettings(option.value)}
              />
            </div>
          )}

          {currentUser?.is_super_admin && privacySettings === 'SHARABLE' && (
            <div className="form-element">
              <label className={styles.label} htmlFor="workspaces">
                {t('Workspaces')}
              </label>
              <Async
                cacheOptions
                defaultOptions={workspaces}
                isMulti={true}
                isClearable={true}
                loadOptions={workspacesPromiseOptions}
                value={selectedWorkspaces}
                placeholder={t('Please enter at least 1 symbol to search')}
                noOptionsMessage={() => t('Please enter at least 1 symbol')}
                loadingMessage={() => t('Loading...')}
                id='workspaces'
                onChange={(options) => {
                  setSelectedWorkspaces(options);
                }}
                required={currentUser?.is_super_admin && privacySettings === 'SHARABLE'}
              />
            </div>
          )}
          <div className="error-message">{t(error)}</div>
          <div className="form-element">
            <div className="submit-wrapper">
              <button
                type="submit"
                className="new-button"
                disabled={isSavingGroupInfo}
                aria-busy={isSavingGroupInfo}
              >
                {isSavingGroupInfo ? t('Saving...') : t('Save')}
              </button>
            </div>
          </div>
        </form>
      )
    },
  ]

  if(!group?.owned_by_workspace) {
    window.location.href = `/groups/${groupId}`;
  }


  return (
    <div className={styles.root}>
      <div className="page-header">
        <div className="breadcrumb">
          <span>
            <Link to="/groups">{t('Actor groups')}</Link>
          </span>
          <span>
            <Link to={`/groups/${group.id}`}>{group.name}</Link>
          </span>
          <span>{t('Edit')}</span>
        </div>
        <div className="controls">
          {currentUser?.is_super_admin === true ? (
            <Tooltip content={t('Copy')} position="bottom">
              <button
                type="button"
                // className={cn('new-button')}
                // onClick={() =>
                //   setCopyGroupModal({
                //     isActive: true,
                //   })
                // }
              >
                <CopyIcon />
              </button>
            </Tooltip>
          ) : (
            ''
          )}
        </div>
      </div>

      <div className="next-card-header">
        <div className="row">
          <h2></h2>
          {selectedSources.length > 0 || isPopulating ? (
            <div className={bulkPanelStyles.bulkButtonsWrapper} id="bulk">
              {!isPopulating && (
                <button>
                  {areAllSourcesSelected ? sources?.total : selectedSources.length} {t('selected')}{' '}
                  <span
                    className={bulkPanelStyles.clear}
                    onClick={() => { clearAllSelectedSources(); setAreAllSourcesSelected(false); }}
                  >
                    <CloseIcon />
                  </span>{' '}
                </button>
              )}
              {!isPopulating && (
                <button
                  onClick={() =>
                    setCopyGroupModal({
                      isActive: true,
                      sources: selectedSources,
                    })
                  }
                >
                  <CopyIcon />
                  {t('Copy to group')}
                </button>
              )}
              <button
                disabled={isPopulating}
                onClick={() => handleAddingSourcesToGroup(selectedSources)}
              >
                <AddToIcon />
                {isPopulating ? t('Adding...') : t('Add')}
              </button>
              {!isPopulating && (
                <button
                  disabled={true}
                  onClick={() => handleAddingSourcesToGroup(selectedSources)}
                >
                  <DeleteIcon />
                  {t('Delete')}
                </button>
              )}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>

      <Tabs
        tabslist={tabslist}
        onIndexChange={(index) => {
          clearAllSelectedSources()
          setAreAllSourcesSelected(false)

          dispatch({type: 'CHANGE_TAB', tabIndex: index})

          dispatch({type: 'APPLY_FILTER', payload: {
            sorting: {isAscending: false, fieldName: sortingFieldNamesBack['relevance']},
            searchQuery: '',
            platforms: [],
            countries: [],
            audience_lte: null,
            audience_gte: null,
            affiliationCountries: [],
            IoCs: [],
            IoCsExclude: false,
            showOnlyBots: false,
            sourceTags: [],
            sourceTagsExclude: false,
            affiliationCountriesExclude: false,
            countriesExclude: false,
            audienceExclude: false,
            geoGroup: [],
            geoGroupExclude: false,
            botsExclude: false,
            affiliationTypes: [],
            affiliationTypesExclude: false,
            status: DEFAULT_STATUSES,
            page: 1,
          }})

          setSearchParams({ q: '', page: 1});
          
        }}
      />

      {copyGroupModal.isActive && (
        <AddToSourceGroupModal
          isOpen={copyGroupModal.isActive}
          sources={copyGroupModal.sources}
          onChange={(isOpen) => setCopyGroupModal({ isActive: isOpen, sources: [] })}
          onSuccess={clearAllSelectedSources}
        />
      )}
      <ReactTooltip id="add-source" place="bottom"/>
    </div>
  );
};
